import React, { useRef, useEffect, useState } from "react";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router";
import { useLocation } from "react-router-dom";
import { useQueryClient } from '@tanstack/react-query';

import {
  Button,
  Grid,
  TextField,
  MenuItem,
  Select,
  FormControl,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  IconButton,
  Typography,
  Box,
  Chip,
  Alert,
  FormHelperText,
  InputLabel,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteIcon from "@mui/icons-material/Delete";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import API from "../../utils/Axios.utils";
import { Notification } from "../../utils/Notification";

import useAuth from "../../hooks/useAuth";
import { useAutofillFromResume } from "../../hooks/useCandidateManagement";
import { mobileClean } from "../../utils/mobileClean";

// Shared by submit validation and the post-autofill check, so autofill can never
// report success on a value that submit will then reject.
const MOBILE_REGEX = /^\+91[6-9]\d{9}$/;
const MOBILE_ERROR = "Mobile must start with +91 followed by 10 digits starting with 6-9.";
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const EMAIL_ERROR = "Email must be valid (contain '@' and a domain name with a dot).";
const AUTOFILL_FIELDS = ["candidate_name", "email", "mobile", "candidate_current_location", "gender"];

const isDocFile = (val) => typeof val === 'string' && (val.includes('.') || val.includes('/') || val.includes('\\') || val.includes('http'));

const getDisplayGender = (genderVal) => {
  if (!genderVal) return "";
  const str = String(genderVal).trim().toLowerCase();
  if (str === "male" || str === "m") return "Male";
  if (str === "female" || str === "f") return "Female";
  if (str === "prefer not to identify") return "Prefer not to identify";
  return genderVal;
};

const AddCandidate = () => {

  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const jobCod = location.state?.jobCode || location.state?.prefilledJobCode;
  const [selectedJobCode, setSelectedJobCode] = useState(jobCod || "");
  const [jobCode, setJobCode] = useState([]);
  const [jobSkills, setJobSkills] = useState(null);
  const [selectedTechSkills, setSelectedTechSkills] = useState([]);
  const [openingsCount, setOpeningsCount] = useState(4);
  const [candidates, setCandidates] = useState([]); // Start empty
  const [filteredCount, setFilteredCount] = useState(0);
  const [expanded, setExpanded] = useState({});
  const [files, setFiles] = useState({});
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const [alert, setAlert] = useState(false);
  const [alertContent, setAlertContent] = useState("");
  const [alertType, setAlertType] = useState("");
  const [validationErrors, setValidationErrors] = useState({});
  const fileInputRefs = useRef({});
  // Latest candidates for async callbacks (autofill resolves after the user may have typed)
  const candidatesRef = useRef(candidates);
  candidatesRef.current = candidates;

  const { token, supplierId, role } = useAuth();
  const isAdmin = role?.toLowerCase() === "admin";
  // eslint-disable-next-line no-unused-vars
  const [selectedSupplierId, setSelectedSupplierId] = useState(location.state?.selectedSupplierId || supplierId || "");
  // eslint-disable-next-line no-unused-vars
  const [supplierData, setSupplierData] = useState([]);
  const { mutateAsync: autofillResume } = useAutofillFromResume();

  const config = React.useMemo(() => ({
    headers: { Authorization: `Bearer ${token}` },
  }), [token]);

  const handleAlert = () => {
    setAlert(false);
  };

  // ... (handleFileChange and other valid functions stay same, skipping for brevity in replacement if possible, but replace_file_content needs contiguous block)
  // Actually, I should just replace the specific function blocks or the hooks area.

  // Let's verify line numbers. config is around line 61.
  // getJobCode starts around line 160.
  // getSkills starts around line 173.

  // I will make separate replacements.




  // const handleFileChange = (event, docType) => {
  //   const selectedFile = event.target.files[0];
  //   if (selectedFile && selectedFile.type === "application/pdf") {
  //     setFiles((prevFiles) => ({
  //       ...prevFiles,
  //       [docType]: selectedFile,
  //     }));
  //   } else {
  //     setAlertContent("Invalid file type. Please select a PDF file.");
  //     setAlertType("error");
  //     setAlert(true);
  //   }
  // };

  const handleFileChange = (event, docType) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      const isIdentityDoc = docType.startsWith("PAN_") || docType.startsWith("Aadhar_");
      const allowedMimeTypes = [
        'application/msword', // .doc        
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
        'application/pdf' // .pdf
      ];
      const allowedExtensions = ['.doc', '.docx', '.pdf'];

      if (isIdentityDoc) {
        allowedMimeTypes.push('image/jpeg', 'image/png', 'image/jpg');
        allowedExtensions.push('.jpeg', '.jpg', '.png');
      }

      const isValidMime = allowedMimeTypes.includes(selectedFile.type);
      const fileExtension = '.' + selectedFile.name.split('.').pop().toLowerCase();
      const isValidExtension = allowedExtensions.includes(fileExtension);

      if (isValidMime || isValidExtension) {
        if (selectedFile.size > 5 * 1024 * 1024) {
          setAlertContent("File too large. Please select a document under 5MB.");
          setAlertType("error");
          setAlert(true);
          event.target.value = ''; // Clear input
          return;
        }

        setFiles((prevFiles) => ({
          ...prevFiles,
          [docType]: selectedFile,
        }));

        if (docType.startsWith("Resume_")) {
          const index = parseInt(docType.split("_")[1]);
          setCandidates((prev) => {
            const copy = [...prev];
            if (copy[index]) {
              copy[index] = {
                ...copy[index],
                profile_path: undefined,
                s3Key_profile_path: undefined
              };
            }
            return copy;
          });
        }
      } else {
        setAlertContent(`Invalid file type. ${isIdentityDoc ? 'Please select a Word, PDF, or Image file.' : 'Please select a Word document (.doc or .docx) or a PDF (.pdf).'}`);
        setAlertType("error");
        setAlert(true);
        event.target.value = ''; // Clear invalid file from input
      }
    }
  };

  const handleAutofill = (index) => {
    const selectedFile = files[`Resume_${index}`];
    if (!selectedFile) {
      setAlertContent("Please select a resume file first.");
      setAlertType("warning");
      setAlert(true);
      return;
    }

    // Trigger Autofill
    autofillResume(selectedFile).then((data) => {
      if (data) {
        const hasData = AUTOFILL_FIELDS.some((field) => data[field]);

        if (!hasData) {
          setAlertContent("Could not extract details from this resume. Please fill manually.");
          setAlertType("warning");
          setAlert(true);
          return;
        }

        const extracted = { ...data, mobile: data.mobile ? mobileClean(data.mobile) : data.mobile };

        // Only fill fields the user left empty -- never overwrite what they typed
        const isEmpty = (value) => !value || !String(value).trim();
        const current = candidatesRef.current[index] || {};
        const filledFields = AUTOFILL_FIELDS.filter((field) => extracted[field] && isEmpty(current[field]));

        setCandidates((prev) => {
          const copy = [...prev];
          if (copy[index]) {
            const updated = { ...copy[index] };
            AUTOFILL_FIELDS.forEach((field) => {
              if (extracted[field] && isEmpty(updated[field])) updated[field] = extracted[field];
            });
            copy[index] = updated;
          }
          return copy;
        });

        // Run the same checks submit will run on the values autofill just put in
        const problems = [];
        const fieldErrors = {};
        if (filledFields.includes("mobile") && !MOBILE_REGEX.test(extracted.mobile)) {
          problems.push("mobile number is not a valid +91 number");
          fieldErrors[`${index}_mobile`] = MOBILE_ERROR;
        }
        if (filledFields.includes("email") && !EMAIL_REGEX.test(extracted.email)) {
          problems.push("email address is not valid");
          fieldErrors[`${index}_email`] = EMAIL_ERROR;
        }

        setValidationErrors((prev) => {
          const next = { ...prev };
          filledFields.forEach((field) => delete next[`${index}_${field}`]);
          return { ...next, ...fieldErrors };
        });

        if (problems.length > 0) {
          setAlertContent(`Autofilled from resume. Please check: ${problems.join(", ")}.`);
          setAlertType("warning");
        } else if (filledFields.length === 0) {
          setAlertContent("All fields were already filled, so nothing was changed.");
          setAlertType("info");
        } else {
          setAlertContent("Resume data autofilled successfully.");
          setAlertType("success");
        }
        setAlert(true);
      }
    }).catch((err) => {
      console.error("Autofill failed", err);
      setAlertContent("Failed to extract data from resume.");
      setAlertType("warning");
      setAlert(true);
    });
  };


  const handleRemoveFile = (docType) => {
    setFiles((prevFiles) => {
      const newFiles = { ...prevFiles };
      delete newFiles[docType];
      return newFiles;
    });

    const index = parseInt(docType.split("_")[1]);
    setCandidates((prev) => {
      const copy = [...prev];
      if (copy[index]) {
        copy[index] = {
          ...copy[index],
          profile_path: undefined,
          s3Key_profile_path: undefined,
        };
      }
      return copy;
    });
  };

  const addCandidate = () => {
    const newIndex = candidates.length;
    setCandidates([...candidates, {}]);
    setExpanded((prev) => ({ ...prev, [`panel${newIndex}`]: true }));
  };

  const removeCandidate = (index) => {
    const newCandidates = candidates.filter((_, i) => i !== index);
    setCandidates(newCandidates);

    // Remove associated file
    const newFiles = { ...files };
    delete newFiles[`Resume_${index}`];
    setFiles(newFiles);

    // Update expanded state
    const newExpanded = { ...expanded };
    delete newExpanded[`panel${index}`];
    setExpanded(newExpanded);
  };

  const toggleAccordion = (index) => {
    setExpanded((prev) => ({
      ...prev,
      [`panel${index}`]: !prev[`panel${index}`],
    }));
  };

  const updateCandidateField = (index, field, value) => {
    setCandidates((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], [field]: value };
      return copy;
    });
  };

  const fetchSuppliers = React.useCallback(async () => {
    try {
      const res = await API.get("/api/users/getallsupplier", config);
      setSupplierData(res.data.supplier || []);
    } catch (error) {
      console.log("Error fetching suppliers:", error);
    }
  }, [config]);

  const getJobCode = React.useCallback(async () => {
    // If supplier, filter by their ID. If Admin, get all (or depends on backend, but let's keep it simple)
    let url = `/api/job/jobcode?supplierId=${selectedSupplierId}`;
    try {
      const res = await API.get(url, config);
      setJobCode(res.data.result);
    } catch (error) {
      console.log(error);
      setAlertContent(error?.response?.data?.message);
      setAlertType("error");
      setAlert(true);
    }
  }, [selectedSupplierId, config]);

  const getSkills = React.useCallback(async () => {
    if (!selectedJobCode) return;

    const prefillCandidates = location.state?.prefillCandidates;
    const emails = prefillCandidates?.map(c => c.email) || [];
    let url = `/api/job/skills/${selectedJobCode}`;
    if (emails.length > 0) {
      url += `?emails=${encodeURIComponent(emails.join(","))}`;
    }
    try {
      const res = await API.get(url, config);
      const data = res.data;
      setJobSkills(data);
      setSelectedTechSkills(data.techSkills || []);
      setOpeningsCount(data.openings);

      if (prefillCandidates) {
        let excludedCount = 0;
        const updatedCandidates = prefillCandidates.map((c, i) => {
          const current = candidates[i] || {};
          const mergedNoticePeriod = (current.notice_period !== undefined && current.notice_period !== null && current.notice_period !== "")
            ? current.notice_period
            : ((c.notice_period !== undefined && c.notice_period !== null && c.notice_period !== "")
              ? c.notice_period
              : (current.timeline || c.timeline || ""));

          const isTargetBlueCollar = data.pipeline_type === 'non_resume_based';
          let mergedBudgetEom = current.budget_eom || c.budget_eom || "";

          if (isTargetBlueCollar) {
            // If target is blue collar and budget UOM is empty or 'perMonth', default to 'perHour'
            if (!mergedBudgetEom || mergedBudgetEom === 'perMonth') {
              mergedBudgetEom = 'perHour';
            }
          } else {
            // If target is white collar and budget UOM is empty or 'perHour', default to 'perMonth'
            if (!mergedBudgetEom || mergedBudgetEom === 'perHour') {
              mergedBudgetEom = 'perMonth';
            }
          }

          return {
            ...c,
            ...current,
            gender: current.gender || c.gender || "",
            notice_period: mergedNoticePeriod,
            budget_eom: mergedBudgetEom,
            profile_path: current.profile_path || c.profile_path || "Existing Resume",
            s3Key_profile_path: current.s3Key_profile_path || c.s3Key_profile_path || current.profile_path || c.profile_path || "",
            alreadyApplied: data.alreadyApplied?.includes(c.email || current.email)
          };
        }).filter(c => {
          if (c.alreadyApplied) {
            excludedCount++;
            return false;
          }
          return true;
        });

        setFilteredCount(excludedCount);
        setCandidates(updatedCandidates);

        const expandedPanels = {};
        updatedCandidates.forEach((_, idx) => {
          expandedPanels[`panel${idx}`] = true;
        });
        setExpanded(expandedPanels);
      } else {
        setFilteredCount(0);
        setCandidates(prev => (prev && prev.length > 0 && Object.keys(prev[0]).length > 0) ? prev : [{}]);
        setExpanded({ "panel0": true });
      }
    } catch (error) {
      console.log(error);
      setAlertContent(error?.response?.data?.message);
      setAlertType("error");
      setAlert(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedJobCode, config]);

  const normalizedLocations = React.useMemo(() => {
    const loc = jobSkills?.locations;

    if (!loc) return [];

    if (Array.isArray(loc)) return loc;

    if (typeof loc === "string") {
      try {
        const parsed = JSON.parse(loc);
        if (Array.isArray(parsed)) return parsed;
        return [parsed];            // parsed is a single string
      } catch {
        // plain string like "Pune"
        return [loc];
      }
    }

    return [];
  }, [jobSkills?.locations]);

  const handleBulkSubmit = async () => {
    // Guard against double-click / re-submit while a request is already in flight.
    if (isFormSubmitted) return;

    // Collect valid candidates
    const validCandidates = [];
    const fileKeys = [];
    const panFileKeys = [];
    const aadharFileKeys = [];
    const errorsObj = {};

    for (let index = 0; index < candidates.length; index++) {
      const cand = candidates[index];


      const fileKey = `Resume_${index}`;
      const name = cand.candidate_name || document.querySelector(`[name="candidate_name_${index}"]`)?.value || "";
      const locationVal = cand.candidate_current_location || document.querySelector(`[name="candidate_current_location_${index}"]`)?.value || "";
      const mobile = cand.mobile || document.querySelector(`[name="mobile_${index}"]`)?.value || "";
      const email = cand.email || document.querySelector(`[name="email_${index}"]`)?.value || "";
      const currency = cand.currency || document.querySelector(`[name="currency_${index}"]`)?.value || "";
      const budget = cand.budget !== undefined && cand.budget !== null ? String(cand.budget) : (document.querySelector(`[name="budget_${index}"]`)?.value || "");
      const budget_eom = cand.budget_eom || document.querySelector(`[name="budget_eom_${index}"]`)?.value || "";
      const notice_period = (cand.notice_period !== undefined && cand.notice_period !== null && cand.notice_period !== "")
        ? cand.notice_period
        : (cand.timeline || document.querySelector(`[name="notice_period_${index}"]`)?.value || "");
      const important_note = cand.important_note || document.querySelector(`[name="important_note_${index}"]`)?.value || "";

      // 1. Required form fields checks
      if (!name) {
        errorsObj[`${index}_candidate_name`] = "Name is required.";
      }
      if (!locationVal) {
        errorsObj[`${index}_candidate_current_location`] = "Location is required.";
      }
      if (!mobile) {
        errorsObj[`${index}_mobile`] = "Mobile number is required.";
      }
      if (!email) {
        errorsObj[`${index}_email`] = "Email is required.";
      }
      const aadharNum = ((cand.aadhar_doc && !isDocFile(cand.aadhar_doc) ? cand.aadhar_doc : cand.aadhar_number) || "").trim();
      if (aadharNum && aadharNum.replace(/\D/g, "").length !== 12) {
        errorsObj[`${index}_aadhar_number`] = "Aadhaar Number must be exactly 12 digits.";
      }

      if (!cand.gender) {
        errorsObj[`${index}_gender`] = "Gender is required.";
      }
      const resumeRequired = jobSkills?.pipeline_type !== 'non_resume_based';
      const hasExistingResume = !!(
        (cand.s3Key_profile_path && cand.s3Key_profile_path !== "Existing Resume") ||
        (cand.profile_path && cand.profile_path !== "Existing Resume") ||
        cand.url ||
        cand.resume_url
      );
      if (resumeRequired && !files[fileKey] && !hasExistingResume) {
        errorsObj[`${index}_resume`] = "Resume is required.";
      }

      // Get form values from nested formik refs (simplified validation)
      const candidateData = {
        candidate_name: name,
        candidate_current_location: locationVal,
        mobile: mobile,
        email: email,
        aadhar_doc: aadharNum,
        aadhar_number: aadharNum,
        currency: currency,
        budget: budget,
        budget_eom: budget_eom,
        notice_period: notice_period,
        important_note: important_note,
        coreSkillId: jobSkills?.coreSkillId || jobSkills?.coreSkills?.id || cand.coreSkillId || "",
        techSkills: cand.techSkills || selectedTechSkills,
        gender: cand.gender || null,
        profile_path: files[fileKey] ? "" : (cand.profile_path === "Existing Resume" ? "" : (cand.profile_path || cand.resume_url || cand.url || "")),
        s3Key_profile_path: files[fileKey] ? "" : (cand.s3Key_profile_path === "Existing Resume" ? "" : (cand.s3Key_profile_path || cand.profile_path || cand.resume_url || cand.url || "")),
        hasPanFile: !!files[`PAN_${index}`],
        hasAadharFile: !!files[`Aadhar_${index}`]
      };

      if (candidateData.candidate_name && candidateData.email && candidateData.mobile) {
        // 2. Format & Pattern checks
        if (name) {
          if (/^\s/.test(name)) {
            errorsObj[`${index}_candidate_name`] = "Name should not start with a space.";
          } else if (!/^[A-Za-z\s]+$/.test(name)) {
            errorsObj[`${index}_candidate_name`] = "Name must only contain letters and spaces.";
          }
        }
        if (locationVal) {
          if (/^\s/.test(locationVal)) {
            errorsObj[`${index}_candidate_current_location`] = "Location should not start with a space.";
          }
        }
        if (mobile) {
          if (!MOBILE_REGEX.test(mobile)) {
            errorsObj[`${index}_mobile`] = MOBILE_ERROR;
          }
        }
        if (email) {
          if (!EMAIL_REGEX.test(email)) {
            errorsObj[`${index}_email`] = EMAIL_ERROR;
          }
        }

        const hasCurrentCandidateErrors = Object.keys(errorsObj).some(key => key.startsWith(`${index}_`));
        if (!hasCurrentCandidateErrors) {
          validCandidates.push(candidateData);

          if (files[fileKey]) {
            fileKeys.push(fileKey);
          }
          if (files[`PAN_${index}`]) {
            panFileKeys.push(`PAN_${index}`);
          }
          if (files[`Aadhar_${index}`]) {
            aadharFileKeys.push(`Aadhar_${index}`);
          }
        }
      }
    }

    if (Object.keys(errorsObj).length > 0) {
      setValidationErrors(errorsObj);
      setAlertContent("Please correct the errors highlighted in red.");
      setAlertType("error");
      setAlert(true);
      return;
    }

    setValidationErrors({});

    if (validCandidates.length === 0) {
      setAlertContent("Please fill at least one valid candidate.");
      setAlertType("error");
      setAlert(true);
      return;
    }

    // Create FormData
    const formData = new FormData();
    formData.append("job_code", selectedJobCode);
    formData.append("candidates", JSON.stringify(validCandidates));
    formData.append("suppliercode", selectedSupplierId);

    // Append files
    fileKeys.forEach((key) => {
      if (files[key]) {
        formData.append("resumes", files[key]);
      }
    });
    panFileKeys.forEach((key) => {
      if (files[key]) {
        formData.append("pan_docs", files[key]);
      }
    });
    aadharFileKeys.forEach((key) => {
      if (files[key]) {
        formData.append("aadhar_docs", files[key]);
      }
    });

    setIsFormSubmitted(true);
    try {
      const res = await API.post("/api/profile/addbulkprofile", formData, config);
      const result = res.data; // Already unwrapped by interceptor

      if (result.errors > 0) {
        // Construct detailed error message
        const errorDetails = result.error_details?.map(err =>
          `${err.email !== 'unknown' ? err.email + ': ' : ''}${err.error}`
        ).join(" | ");

        setAlertContent(result.created > 0
          ? `${result.created} Successful, ${result.errors} Failed: ${errorDetails}`
          : `Failed: ${errorDetails}`
        );
        setAlertType(result.created > 0 ? "warning" : "error");
      } else {
        setAlertContent(result.message || `${validCandidates.length} candidate${validCandidates.length === 1 ? '' : 's'} submitted successfully`);
        setAlertType("success");
      }
      setAlert(true);

      // Invalidate React Query cache
      queryClient.invalidateQueries({ queryKey: ['candidates'] });
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      queryClient.invalidateQueries({ queryKey: ['duplicatePairs'] });

      if (result.created > 0) {
        setTimeout(() => {
          const from = location.state?.from;
          if (from) {
            const isInHistory = from.includes("adminAccordion") || from.includes("admindashboard") || from.includes("suppliermanagement") || from.includes("supplierdashboard");
            if (isInHistory) {
              navigate(-1);
              return;
            }
            const isFullPath = from.startsWith("/");
            if (isFullPath) {
              let targetPath = from;
              const isCandidateData = targetPath.startsWith("/candidate_data");
              if (isCandidateData) {
                targetPath = targetPath.split('?')[0];
              }
              navigate(targetPath, { replace: true, state: isCandidateData ? {} : { jobCode: selectedJobCode || jobCod } });
            } else {
              const cleanFrom = from.split('?')[0];
              navigate("/candidate_data" + cleanFrom, { replace: true, state: {} });
            }
          } else {
            navigate("/candidate_data");
          }
        }, 2000);
      }
    } catch (error) {
      console.error("Bulk upload error:", error);
      setAlertContent(error.response?.data?.message || error.message || "Upload failed");
      setAlertType("error");
      setAlert(true);
    } finally {
      // Re-enable the button so the user can correct errors and resubmit;
      // on success the screen navigates away shortly after anyway.
      setIsFormSubmitted(false);
    }
  };



  useEffect(() => {
    if (isAdmin) { // Admin
      fetchSuppliers();
    }
  }, [isAdmin, fetchSuppliers]);

  useEffect(() => {
    if (selectedSupplierId || isAdmin) {
      getJobCode();
    }
  }, [selectedSupplierId, isAdmin, getJobCode]);

  useEffect(() => {
    if (selectedJobCode) {
      getSkills();
    }
  }, [selectedJobCode, getSkills]);

  useEffect(() => {
    const clearLocationState = () => {
      navigate({ ...location, state: {} });
    };
    window.addEventListener("beforeunload", clearLocationState);
    return () => {
      window.removeEventListener("beforeunload", clearLocationState);
    };
  }, [navigate, location]);

  const navigateToPreviousPage = () => {
    // If they manually selected a job here and want to go back to the job selection step
    if (selectedJobCode && !jobCod) {
      setSelectedJobCode("");
      setJobSkills(null);
      return;
    }
    const from = location.state?.from || "";
    if (from) {
      // These are pages that navigated to us via push (they're already in history).
      // Use navigate(-1) to go back naturally and avoid duplicating history entries.
      const isInHistory = from.includes("adminAccordion") || from.includes("admindashboard") || from.includes("suppliermanagement") || from.includes("supplierdashboard");
      if (isInHistory) {
        navigate(-1);
        return;
      }
      const isFullPath = from.startsWith("/");
      if (isFullPath) {
        let targetPath = from;
        const isCandidateData = targetPath.startsWith("/candidate_data");
        if (isCandidateData) {
          targetPath = targetPath.split('?')[0];
        }
        navigate(targetPath, { replace: true, state: isCandidateData ? {} : { jobCode: selectedJobCode || jobCod } });
      } else {
        const cleanFrom = from.split('?')[0];
        navigate("/candidate_data" + cleanFrom, { replace: true, state: {} });
      }
    } else {
      navigate(-1);
    }
  };

  const allCurrency = [
    { id: 1, value: "INR", label: "INR" },
    { id: 2, value: "USD", label: "USD" },
    { id: 3, value: "GBP", label: "GBP" },
  ];

  const budgetUOM = [
    { id: 1, value: "perHour", label: "Per Hour" },
    { id: 2, value: "perMonth", label: "Per Month" },
    { id: 3, value: "perDay", label: "Per Day" },
  ];

  const FileUploadSection = ({ index }) => {
    const candidate = candidates[index];
    const hasExistingResume = !!(
      (candidate?.s3Key_profile_path && candidate.s3Key_profile_path !== "Existing Resume") ||
      (candidate?.profile_path && candidate.profile_path !== "Existing Resume") ||
      candidate?.url ||
      candidate?.resume_url
    );
    const cleanDocName = (rawName) => {
      if (!rawName || typeof rawName !== 'string') return '';
      let name = rawName.split('?')[0];
      if (name.includes('/') || name.includes('\\')) {
        try {
          name = decodeURIComponent(name.split('/').pop().split('\\').pop());
        } catch {
          name = name.split('/').pop().split('\\').pop();
        }
      }
      if (candidate?.job_code && name.startsWith(candidate.job_code + '_')) {
        name = name.substring(candidate.job_code.length + 1);
      }
      while (/^[A-Za-z0-9]*[0-9-][A-Za-z0-9]*_/.test(name)) {
        name = name.replace(/^[A-Za-z0-9]*[0-9-][A-Za-z0-9]*_/, '');
      }
      return name;
    };

    const existingFileName = cleanDocName(
      (candidate?.profile_path && candidate.profile_path !== "Existing Resume")
        ? candidate.profile_path
        : ((candidate?.s3Key_profile_path && candidate.s3Key_profile_path !== "Existing Resume")
          ? candidate.s3Key_profile_path
          : (candidate?.url || candidate?.resume_url || ""))
    );

    const isDocFileLocal = (val) => typeof val === 'string' && (val.includes('.') || val.includes('/') || val.includes('\\') || val.includes('http'));

    const hasExistingPan = !!(candidate?.s3Key_pan_doc || (candidate?.pan_doc && isDocFileLocal(candidate.pan_doc)));
    const existingPanName = cleanDocName(
      (candidate?.pan_doc && isDocFileLocal(candidate.pan_doc))
        ? candidate.pan_doc
        : (candidate?.s3Key_pan_doc || "")
    );

    const hasExistingAadhar = !!(candidate?.s3Key_aadhar_doc || (candidate?.aadhar_doc && isDocFileLocal(candidate.aadhar_doc)));
    const existingAadharName = cleanDocName(
      (candidate?.aadhar_doc && isDocFileLocal(candidate.aadhar_doc))
        ? candidate.aadhar_doc
        : (candidate?.s3Key_aadhar_doc || "")
    );

    const renderFileInput = (docType, label, required, hasExisting, existingName, acceptStr) => {
      const fileKey = `${docType}_${index}`;
      const isError = validationErrors[`${index}_${docType.toLowerCase()}`];
      return (
        <Grid container spacing={2} alignItems="center" mt={1}>
          <Grid item xs={12} md={3}>
            <Typography variant="body2" fontWeight={500} color={isError ? "error" : "textPrimary"}>
              {label} {required && <span style={{ color: 'red' }}>*</span>}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            {files[fileKey] ? (
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="body2" noWrap style={{ maxWidth: 200 }}>
                  {files[fileKey].name}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => handleRemoveFile(fileKey)}
                  title="Remove file"
                >
                  <DeleteIcon fontSize="small" color="error" />
                </IconButton>
              </Box>
            ) : hasExisting ? (
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="body2" noWrap style={{ maxWidth: 200, color: '#2e7d32', fontWeight: 500 }}>
                  📄 {existingName} (Existing)
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => handleRemoveFile(fileKey)}
                  title="Remove file"
                >
                  <DeleteIcon fontSize="small" color="error" />
                </IconButton>
              </Box>
            ) : (
              <Typography variant="body2" color={isError ? "error" : "textSecondary"}>
                {isError || "No file selected"}
              </Typography>
            )}
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box display="flex" gap={1}>
              <Button
                variant="outlined"
                size="small"
                fullWidth
                color={isError ? "error" : "primary"}
                onClick={() => fileInputRefs.current[fileKey]?.click()}
              >
                Browse
              </Button>
              {docType === "Resume" && (
                <Button
                  variant="contained"
                  color="secondary"
                  size="small"
                  fullWidth
                  onClick={() => handleAutofill(index)}
                  disabled={!files[fileKey]}
                >
                  Autofill
                </Button>
              )}
            </Box>
            <input
              type="file"
              hidden
              accept={acceptStr}
              onChange={(e) => handleFileChange(e, fileKey)}
              ref={(el) => {
                if (el) fileInputRefs.current[fileKey] = el;
              }}
            />
          </Grid>
        </Grid>
      );
    };

    return (
      <div style={{
        marginTop: 20,
        border: validationErrors[`${index}_resume`] ? "1.5px solid #d32f2f" : "1px solid #e0e0e0",
        borderRadius: "8px",
        padding: "16px",
        backgroundColor: "#fafafa"
      }}>
        {jobSkills?.pipeline_type !== 'non_resume_based' && renderFileInput("Resume", "Upload Resume", true, hasExistingResume, existingFileName, ".doc,.docx,.pdf")}
        {renderFileInput("PAN", "Upload PAN Document", false, hasExistingPan, existingPanName, ".doc,.docx,.pdf,.jpg,.jpeg,.png")}
        {renderFileInput("Aadhar", "Upload Aadhar Document", false, hasExistingAadhar, existingAadharName, ".doc,.docx,.pdf,.jpg,.jpeg,.png")}
      </div>
    );
  };

  return (
    <div style={{ marginTop: 20 }}>
      <Button startIcon={<ArrowBackIcon />} onClick={navigateToPreviousPage} sx={{ mb: 2 }}>
        Back
      </Button>

      <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
        <Typography variant="h5" fontWeight={600} gutterBottom mb={3}>
          Add Profiles
        </Typography>

        {filteredCount > 0 && (
          <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
            {filteredCount} candidate(s) were excluded because they have already been assigned to this job.
          </Alert>
        )}



        {/* Job Code Selection */}
        <Grid container spacing={3} mb={4}>
          <Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
            <FormControl fullWidth size="small">
              <InputLabel id="job-code-label">Job Code</InputLabel>
              <Select
                labelId="job-code-label"
                value={selectedJobCode}
                onChange={(e) => setSelectedJobCode(e.target.value)}
                onOpen={getJobCode}
                label="Job Code"
                sx={{
                  '& .MuiSelect-select': {
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }
                }}
              >
                <MenuItem value="" disabled>
                  Select Job Code
                </MenuItem>
                {/* Ensure the currently selected job is in the list even before fetching */}
                {selectedJobCode && !jobCode?.find(j => j.job_code === selectedJobCode) && (
                  <MenuItem value={selectedJobCode}>{selectedJobCode}</MenuItem>
                )}
                {jobCode?.map((job) => (
                  <MenuItem
                    key={job.job_code}
                    value={job.job_code}
                    sx={{
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                      maxWidth: '100%'
                    }}
                  >
                    {job.job_code}{job.title ? ` - ${job.title}` : ""}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>


        {/* Job Details Accordion - Shows only after job code selection */}
        {selectedJobCode && jobSkills && (
          <Accordion defaultExpanded sx={{ mb: 4 }}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="job-details-content"
              id="job-details-header"
            >
              <Typography variant="h6" fontWeight={600}>
                Job Details
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              <Paper elevation={1} sx={{ overflow: "hidden" }}>
                <TableContainer>
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell variant="head" sx={{ fontWeight: 600, width: "30%" }}>Job Code</TableCell>
                        <TableCell>{jobSkills.jobCode || selectedJobCode}</TableCell>
                        <TableCell variant="head" sx={{ fontWeight: 600, width: "30%" }}>Openings</TableCell>
                        <TableCell>
                          <Chip label={jobSkills.openings || openingsCount} color="primary" size="small" />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell variant="head" sx={{ fontWeight: 600 }}>Title</TableCell>
                        <TableCell>{jobSkills.title || 'N/A'}</TableCell>
                        <TableCell variant="head" sx={{ fontWeight: 600 }}>Core Skill</TableCell>
                        <TableCell>{jobSkills?.coreSkills?.name || 'N/A'}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell variant="head" sx={{ fontWeight: 600 }}>Experience</TableCell>
                        <TableCell>{jobSkills.experience || 'N/A'} years</TableCell>
                        <TableCell variant="head" sx={{ fontWeight: 600 }}>Mode</TableCell>
                        <TableCell>{jobSkills.mode_of_work || 'N/A'}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell variant="head" sx={{ fontWeight: 600 }}>Budget</TableCell>
                        <TableCell>
                          {Number(jobSkills.budget) > 0
                            ? `${jobSkills.currency ? `${jobSkills.currency} ` : ''}${jobSkills.budget}${jobSkills.budget_uom ? ` / ${jobSkills.budget_uom}` : ''}`
                            : 'Not set'}
                        </TableCell>
                        <TableCell variant="head" sx={{ fontWeight: 600 }}>Timeline</TableCell>
                        <TableCell>{Number(jobSkills.notice_period) > 0 ? `${jobSkills.notice_period} days` : 'Not set'}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell variant="head" sx={{ fontWeight: 600 }}>Assignment Duration</TableCell>
                        <TableCell colSpan={3}>{jobSkills.assignment_duration_days ? `${jobSkills.assignment_duration_days} days` : 'Not set'}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell variant="head" sx={{ fontWeight: 600 }}>Locations</TableCell>
                        <TableCell>
                          {normalizedLocations.length > 0 ? normalizedLocations.join(", ") : "N/A"}
                        </TableCell>
                        <TableCell variant="head" sx={{ fontWeight: 600 }}>Background Verification</TableCell>
                        <TableCell>{jobSkills.background_verification === 'Yes' ? "Required" : "Not Required"}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell variant="head" sx={{ fontWeight: 600 }}>Tech Skills</TableCell>
                        <TableCell colSpan={3}>
                          <Box display="flex" flexWrap="wrap" gap={0.5}>
                            {selectedTechSkills.map((skill, idx) => (
                              <Chip key={idx} label={skill} size="small" color="secondary" />
                            ))}
                          </Box>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>

              {/* Job Description */}
              {jobSkills.job_description && (
                <Paper elevation={1} sx={{ p: 2, mt: 2 }}>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    Job Description
                  </Typography>
                  <div
                    dangerouslySetInnerHTML={{ __html: jobSkills.job_description }}
                    style={{ fontSize: '0.875rem', lineHeight: 1.5 }}
                  />
                </Paper>
              )}
            </AccordionDetails>
          </Accordion>
        )}


        {/* Candidate Accordions - Only show after job selection */}
        {selectedJobCode && jobSkills && (
          <Box>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
              <Typography variant="h6" fontWeight={600}>
                Candidate Details ({candidates.length})
              </Typography>
            </Box>

            <Grid container spacing={2}>
              {/* Dynamic Accordions for Candidates */}
              {candidates.map((candidate, index) => (
                <Grid item xs={12} key={index}>
                  <Accordion
                    expanded={expanded[`panel${index}`]}
                    onChange={() => toggleAccordion(index)}
                    sx={{ boxShadow: 1, border: 'none' }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography sx={{ width: "90%", flexShrink: 0, fontWeight: 500 }}>
                        Candidate {index + 1}
                        {files[`Resume_${index}`] && (
                          <Chip
                            label={files[`Resume_${index}`].name}
                            size="small"
                            color="success"
                            sx={{ ml: 1 }}
                          />
                        )}
                        {!files[`Resume_${index}`] && candidate.s3Key_profile_path && (
                          <Chip
                            label={`${candidate.profile_path || 'Resume'} (Existing)`}
                            size="small"
                            color="primary"
                            sx={{ ml: 1 }}
                          />
                        )}
                      </Typography>
                      {candidates.length > 1 && (
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            removeCandidate(index);
                          }}
                          size="small"
                          sx={{ ml: 'auto' }}

                        >
                          <DeleteIcon color="error" />
                        </IconButton>
                      )}
                    </AccordionSummary>

                    <AccordionDetails>
                      <Grid container spacing={2}>
                        {/* Candidate Name */}
                        <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                          <Typography variant="body2" fontWeight={500} gutterBottom color={validationErrors[`${index}_candidate_name`] ? "error" : "textPrimary"}>
                            Candidate Name *
                          </Typography>
                          <TextField
                            name={`candidate_name_${index}`}
                            value={candidate.candidate_name || ""}
                            onChange={(e) => updateCandidateField(index, "candidate_name", e.target.value)}
                            fullWidth
                            size="small"
                            required

                            error={!!validationErrors[`${index}_candidate_name`]}
                            helperText={validationErrors[`${index}_candidate_name`]}
                          />
                        </Grid>

                        {/* Gender */}
                        <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                          <Typography variant="body2" fontWeight={500} gutterBottom color={validationErrors[`${index}_gender`] ? "error" : "textPrimary"}>
                            Gender *
                          </Typography>
                          <FormControl fullWidth size="small" error={!!validationErrors[`${index}_gender`]}>
                            <Select
                              value={getDisplayGender(candidate.gender)}
                              onChange={(e) => updateCandidateField(index, "gender", e.target.value)}
                              displayEmpty
                            >
                              <MenuItem value="" disabled>Select Gender</MenuItem>
                              <MenuItem value="Prefer not to identify">Prefer not to identify</MenuItem>
                              <MenuItem value="Male">Male</MenuItem>
                              <MenuItem value="Female">Female</MenuItem>
                            </Select>
                            {validationErrors[`${index}_gender`] && (
                              <FormHelperText>{validationErrors[`${index}_gender`]}</FormHelperText>
                            )}
                          </FormControl>
                        </Grid>

                        {/* Current Location */}
                        <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                          <Typography variant="body2" fontWeight={500} gutterBottom color={validationErrors[`${index}_candidate_current_location`] ? "error" : "textPrimary"}>
                            Location *
                          </Typography>
                          <TextField
                            name={`candidate_current_location_${index}`}
                            value={candidate.candidate_current_location || ""}
                            onChange={(e) => updateCandidateField(index, "candidate_current_location", e.target.value)}
                            fullWidth
                            size="small"
                            required

                            error={!!validationErrors[`${index}_candidate_current_location`]}
                            helperText={validationErrors[`${index}_candidate_current_location`]}
                          />
                        </Grid>

                        {/* Mobile */}
                        <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                          <Typography variant="body2" fontWeight={500} gutterBottom color={validationErrors[`${index}_mobile`] ? "error" : "textPrimary"}>
                            Mobile *
                          </Typography>
                          <TextField
                            name={`mobile_${index}`}
                            value={candidate.mobile || ""}
                            onChange={(e) => updateCandidateField(index, "mobile", e.target.value)}
                            onBlur={(e) => {
                              // Normalise common Indian formats (8771717190, 0877..., 91 877...) to +91XXXXXXXXXX
                              const cleaned = mobileClean(e.target.value);
                              if (cleaned && cleaned !== e.target.value) updateCandidateField(index, "mobile", cleaned);
                            }}
                            placeholder="+91XXXXXXXXXX"
                            fullWidth
                            size="small"
                            required

                            error={!!validationErrors[`${index}_mobile`]}
                            helperText={validationErrors[`${index}_mobile`]}
                          />
                        </Grid>

                        {/* Email */}
                        <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                          <Typography variant="body2" fontWeight={500} gutterBottom color={validationErrors[`${index}_email`] ? "error" : "textPrimary"}>
                            Email *
                          </Typography>
                          <TextField
                            name={`email_${index}`}
                            type="email"
                            value={candidate.email || ""}
                            onChange={(e) => updateCandidateField(index, "email", e.target.value)}
                            fullWidth
                            size="small"
                            required

                            error={!!validationErrors[`${index}_email`]}
                            helperText={validationErrors[`${index}_email`]}
                          />
                        </Grid>

                        {/* Aadhaar Number */}
                        <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                          <Typography variant="body2" fontWeight={500} gutterBottom color={validationErrors[`${index}_aadhar_number`] ? "error" : "textPrimary"}>
                            Aadhaar Number
                          </Typography>
                          <TextField
                            name={`aadhar_number_${Math.random().toString(36).substring(7)}_${index}`}
                            placeholder="Enter 12-digit Aadhaar Number"
                            value={(candidate.aadhar_doc && !isDocFile(candidate.aadhar_doc) ? candidate.aadhar_doc : candidate.aadhar_number) || ""}
                            onChange={(e) => {
                              const val = e.target.value.replace(/[^0-9]/g, "").slice(0, 12);
                              updateCandidateField(index, "aadhar_doc", val);
                              updateCandidateField(index, "aadhar_number", val);
                            }}
                            fullWidth
                            size="small"
                            required
                            autoComplete="nope"
                            inputProps={{ maxLength: 12, autoComplete: 'nope' }}
                            error={!!validationErrors[`${index}_aadhar_number`]}
                            helperText={validationErrors[`${index}_aadhar_number`]}
                          />
                        </Grid>

                        {/* Currency */}
                        <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                          <Typography variant="body2" fontWeight={500} gutterBottom>
                            Currency *
                          </Typography>
                          <FormControl fullWidth size="small">
                            <Select
                              name={`currency_${index}`}
                              value={candidate.currency || ""}
                              onChange={(e) => updateCandidateField(index, "currency", e.target.value)}

                            >
                              {allCurrency.map((currency) => (
                                <MenuItem key={currency.id} value={currency.value}>
                                  {currency.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>

                        {/* Budget */}
                        <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                          <Typography variant="body2" fontWeight={500} gutterBottom>
                            Budget *
                          </Typography>
                          <TextField
                            name={`budget_${index}`}
                            value={candidate.budget || ""}
                            onChange={(e) => updateCandidateField(index, "budget", e.target.value)}
                            fullWidth
                            size="small"
                            required
                            onInput={(e) =>
                              (e.target.value = e.target.value.replace(/[^0-9.]/g, ""))
                            }

                          />
                        </Grid>

                        {/* Budget UOM */}
                        <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                          <Typography variant="body2" fontWeight={500} gutterBottom>
                            Budget UOM *
                          </Typography>
                          <FormControl fullWidth size="small">
                            <Select
                              name={`budget_eom_${index}`}
                              value={candidate.budget_eom || ""}
                              onChange={(e) => updateCandidateField(index, "budget_eom", e.target.value)}

                            >
                              {budgetUOM.map((uom) => (
                                <MenuItem key={uom.id} value={uom.value}>
                                  {uom.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>

                        {/* Notice Period */}
                        <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                          <Typography variant="body2" fontWeight={500} gutterBottom>
                            Timeline (days)
                          </Typography>
                          <TextField
                            name={`notice_period_${index}`}
                            value={
                              (candidate.notice_period !== undefined && candidate.notice_period !== null && candidate.notice_period !== "")
                                ? candidate.notice_period
                                : (candidate.timeline || "")
                            }
                            onChange={(e) => updateCandidateField(index, "notice_period", e.target.value)}
                            fullWidth
                            size="small"
                            type="text"

                          />
                        </Grid>

                        {/* Notes */}
                        <Grid item xs={12}>
                          <Typography variant="body2" fontWeight={500} gutterBottom>
                            Notes (Optional)
                          </Typography>
                          <TextField
                            name={`important_note_${index}`}
                            value={candidate.important_note || ""}
                            onChange={(e) => updateCandidateField(index, "important_note", e.target.value)}
                            multiline
                            rows={3}
                            placeholder="Enter important notes about the candidate..."
                            fullWidth
                            size="small"

                          />
                        </Grid>

                        {/* File Upload */}
                        <Grid item xs={12}>
                          <FileUploadSection index={index} />
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                </Grid>
              ))}

              {/* Add Candidate Button - ALWAYS SHOW (no limit) */}
              <Grid item xs={12}>
                <Button
                  variant="outlined"
                  onClick={addCandidate}
                  startIcon={<AddIcon />}
                  fullWidth
                  size="large"
                >
                  Add Another Candidate
                </Button>
              </Grid>

              {/* Submit Button */}
              <Grid item xs={12}>
                <Box display="flex" justifyContent="center" mt={3}>
                  <Button
                    variant="contained"
                    size="large"
                    disabled={isFormSubmitted || candidates.length === 0}
                    sx={{
                      background: "#2777AA",
                      px: 6,
                      py: 1.5,
                      fontSize: "1rem",
                      fontWeight: 600,
                    }}
                    onClick={handleBulkSubmit}
                  >
                    {isFormSubmitted
                      ? "Submitting..."
                      : `Submit (${candidates.length} Eligible Candidate${candidates.length === 1 ? '' : 's'})`}
                  </Button>
                </Box>
              </Grid>


            </Grid>
          </Box>
        )}
      </Paper>

      <Notification
        message={alertContent}
        open={alert}
        severity={alertType}
        onClose={handleAlert}
      />
    </div>
  );
};

export default AddCandidate;
